import React, { useState, useEffect, useRef } from 'react';
import { getCompanyInfo } from '../../services/migration/companyDataService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Paper,
  IconButton,
  Divider,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth } from '../../contexts/AuthContext';
import html2canvas from 'html2canvas';
import { useTranslation } from 'react-i18next';

interface CompanyQRModalProps {
  open: boolean;
  onClose: () => void;
}

const CompanyQRModal: React.FC<CompanyQRModalProps> = ({ open, onClose }) => {
  const { t, i18n } = useTranslation(['humanResources', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const hr = (key: string, defaultValue: string) => t(key, { ns: 'humanResources', defaultValue });
  const [companyId, setCompanyId] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const qrContainerRef = useRef<HTMLDivElement>(null);
  const { currentUser, userDetails } = useAuth(); // ✅ AuthContext'ten userDetails al
  const theme = useTheme();

  useEffect(() => {
    if (open && userDetails?.companyId) {
      fetchCompanyInfo();
    }
  }, [open, userDetails?.companyId]);

  const fetchCompanyInfo = async () => {
    // ✅ companyId artık AuthContext cache'inden geliyor - Firebase sorgusu YOK!
    if (!userDetails?.companyId) {
      setError(hr('companyQr.errors.companyNotFound', 'Company information was not found for the user'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userCompanyId = userDetails.companyId;
      setCompanyId(userCompanyId);

      // Şirket adını SQL'den al (GET /api/Company)
      const companyInfoData = await getCompanyInfo(userCompanyId);

      if (companyInfoData && (companyInfoData as any).name) {
        setCompanyName((companyInfoData as any).name);
      } else {
        setCompanyName(userDetails.companyName || hr('companyQr.companyFallback', 'Company'));
      }
    } catch (error) {
      console.error('Şirket bilgileri alınırken hata:', error);
      // Fallback: AuthContext'ten şirket adını kullan
      setCompanyName(userDetails.companyName || hr('companyQr.companyFallback', 'Company'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!qrContainerRef.current) return;

    try {
      const canvas = await html2canvas(qrContainerRef.current, {
        backgroundColor: '#FFFFFF',
        scale: 2, // Daha yüksek kalite için
      });

      const image = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = image;
      downloadLink.download = `${companyName}-${hr('companyQr.qrCodeFileSuffix', 'QR-Code')}.png`;
      downloadLink.click();
    } catch (error) {
      console.error('QR kodu indirme hatası:', error);
    }
  };

  const handlePrint = async () => {
    if (!qrContainerRef.current) return;

    try {
      const canvas = await html2canvas(qrContainerRef.current, {
        backgroundColor: '#FFFFFF',
        scale: 2,
      });

      const image = canvas.toDataURL('image/png');

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert(hr('companyQr.errors.disablePopup', 'Please disable the popup blocker and try again'));
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>${hr('companyQr.printTitle', 'Print Company QR Code')}</title>
            <style>
              body { 
                margin: 0; 
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
              }
              img { max-width: 100%; }
              @media print {
                @page { margin: 0; }
                body { margin: 1cm; }
              }
            </style>
          </head>
          <body>
            <img src="${image}" />
            <script>
              setTimeout(() => {
                window.print();
                window.close();
              }, 500);
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    } catch (error) {
      console.error('QR kodu yazdırma hatası:', error);
    }
  };

  const handleShare = async () => {
    if (!qrContainerRef.current || !navigator.share) {
      alert(hr('companyQr.errors.shareUnsupported', 'Share is not supported in this browser'));
      return;
    }

    try {
      const canvas = await html2canvas(qrContainerRef.current, {
        backgroundColor: '#FFFFFF',
        scale: 2,
      });

      const image = canvas.toDataURL('image/png');
      const blob = await (await fetch(image)).blob();
      const file = new File([blob], `${companyName}-${hr('companyQr.qrCodeFileSuffix', 'QR-Code')}.png`, { type: 'image/png' });

      await navigator.share({
        title: `${companyName} ${hr('companyQr.title', 'Company QR Code')}`,
        text: hr('companyQr.description', 'Scan this QR code to register as personnel in our company'),
        files: [file],
      });
    } catch (error: unknown) {
      console.error('QR kodu paylaşma hatası:', error);
      // Paylaşım iptal edildiğinde de hata fırlatır, bu normal bir durum
      if (error && typeof error === 'object' && 'name' in error && error.name !== 'AbortError') {
        alert(hr('companyQr.errors.shareFailed', 'An error occurred while sharing'));
      }
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 2
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" component="div" fontWeight="bold">
          {hr('companyQr.title', 'Company QR Code')}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ textAlign: 'center', p: 4 }}>
            {error}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Paper 
              ref={qrContainerRef}
              elevation={0} 
              sx={{ 
                width: 320, 
                p: 4, 
                backgroundColor: 'white',
                borderRadius: 2,
                border: '1px solid #eee',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}
            >
              <Box sx={{ mb: 3 }}>
                <QRCodeCanvas
                  value={companyId}
                  size={200}
                  level="H" // Yüksek hata düzeltme seviyesi
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                  includeMargin={true}
                />
              </Box>
              
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                {companyName}
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                {hr('companyQr.descriptionLine1', 'Scan this QR code to')}<br />
                {hr('companyQr.descriptionLine2', 'register as personnel in our company')}
              </Typography>
            </Paper>
            
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
              >
                {hr('companyQr.actions.download', 'Download')}
              </Button>
              
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
              >
                {hr('companyQr.actions.print', 'Print')}
              </Button>
              
              {typeof navigator !== 'undefined' && 'share' in navigator && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                >
                  {hr('companyQr.actions.share', 'Share')}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          {hr('common.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyQRModal; 